import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/reducers';
import { Observable } from 'rxjs';

import { PaginationRequest } from 'src/app/models/common/paginationRequest';
import { AppSessionService } from '../app-session/app-session.service';
import { FilterOperation } from 'src/app/models/custom-table/custom-table.model';
import { LimitDTO } from 'src/app/models/limits/limitDTO';

import { GridUtils } from '../../utils/gridUtils';
import { setAdvancedFilterQuery, setSimpleFilterQuery } from '../../utils/tableUtils';

const EMPTY_STRING = '';

@Injectable({
  providedIn: 'root'
})
export class ContractsService {
  private BASE_URL = this.appSession.baseUrl + '/api/v1/contracts';

  constructor(private http: HttpClient, private store: Store<AppState>, private appSession: AppSessionService) { }

  getPlafondRules(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/contracts'}/plafondRules`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + '&limit=' + pageReq.limit + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  getPlafondMeasurements(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-type', 'application/x-www-form-urlencoded; ');

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set('query', pageReq.query);
    }

    let url = `${this.appSession.baseUrl + '/api/v1/contracts'}/plafondMeasurements`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false
      };
    }

    const pagination = '?offset=' + pageReq.offset + '&limit=' + pageReq.limit + '&real_size=' + pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  // create contract
  createContract(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.contract);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}`;
    return this.http.post(url, data, { headers });
  }

  // update contract
  updateContract(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.contract);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/${action.contract.code}`;
    return this.http.put(url, data, { headers });
  }

  // get contract movements
  getContractMovements(contractCode: string, paging?: any, filteringArgs?: any, sortingArgs?: any): Observable<any> {
    const path = '/' + encodeURIComponent(contractCode) + '/movements?';

    let headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let sortedLoadingAction = sessionStorage.getItem('sortposDateDESC');

    let filterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs && filteringArgs.length > 0) {
      filterQuery = `${GridUtils._buildFilteringExpression(filteringArgs)}`;
    }

    if (sortingArgs && sortingArgs.length > 0) {
      sortingArgs.forEach((columnSorting, idx) => {
        if (idx < (sortingArgs.length - 1)) {
          orderQuery += (GridUtils._buildSortExpression(columnSorting) + ';');
        } else {
          orderQuery += GridUtils._buildSortExpression(columnSorting);
        }
      });
    } else {
      orderQuery = sortedLoadingAction;
    }

    if (filterQuery.length > 0) {
      headers = headers.append('query', filterQuery);
    }

    if (orderQuery.length > 0) {
      headers = headers.append('sort', orderQuery);
    }

    return this.http.get(this.buildDataUrl(paging, path), { headers });
  }

  // get contract
  getContract(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/${action.contractCode}`;
    return this.http.get(url, { headers });
  }


  // delete productGroups
  deleteContractProductGroups(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let data = '';

    if (action.productGroupCodes.length > 0) {
      action.productGroupCodes.forEach(productGroupCode => {
        if (data.length === 0) {
          data = data.concat('?productGroupCode=' + productGroupCode);
        } else {
          data = data.concat('&productGroupCode=' + productGroupCode);
        }
      });
    }
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/` + encodeURIComponent(action.contractCode) + `/productGroups` + data;
    return this.http.delete(url, { headers });
  }

  // save contract limits
  saveContractLimits(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.contractLimitCodes);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/` + encodeURIComponent(action.contractCode) + `/limits`;
    return this.http.post(url, data, { headers });
  }

  // delete Contract Limits
  deleteContractLimits(action): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let data = '';
    data = JSON.stringify(action.contractLimitCodes);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/` + encodeURIComponent(action.contractCode) + `/limits`;
    return this.http.request('delete', url, { headers, body: data });
  }

  // update contract associated product groups
  updateContractProductGroup(action: { contractCode: string; productGroups: string[]; type: string }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.productGroups);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/${action.contractCode}/productGroups`;
    return this.http.post(url, data, { headers });
  }

  // update contract associated limits
  updateContractLimits(action: { contractCode: string; limits: LimitDTO[]; type: string }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.limits);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/${action.contractCode}/limits`;
    return this.http.post(url, data, { headers });
  }

  // reset contract limit
  resetContractLimit(action: { contractCode: string; limitCode: string; type: string }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });
    const data = JSON.stringify(action.limitCode);
    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/` + encodeURIComponent(action.contractCode)
      + `/limits/` + encodeURIComponent(action.limitCode) + `/resetLimit`;
    return this.http.post(url, data, { headers });
  }

  // reset contract Plafond
  resetContractPlafond(contractCode): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    const url = `${this.appSession.baseUrl + '/api/v1/contracts'}/` + encodeURIComponent(contractCode)
      + `/resetPlafond`;
    return this.http.post(url, { headers });
  }

  // Get Contracts
  public getContractsList(paging?: any, filteringArgs?: any, sortingArgs?: any, advancedFilteringArgs?:any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-type': 'application/json'
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append('sort', orderQuery);
    }

    return this.http.get(this.buildDataUrl(paging, '/search?'), { headers });
  }

  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.appSession.baseUrl + '/api/v1/contracts'}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += (scrollingQuery !== EMPTY_STRING) ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
